import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Zap, FolderPlus, Upload, Cloud } from 'lucide-react';
import { 
  extractValues, 
  setProposalFile,
  setNoteFile,
  setSecurityFile,
  setProposalFiles,
  setNoteFiles,
  setSecurityFiles,
  setAdditionalFiles,
  setArchiveFiles,
  setChecklistFiles,
  addProposalFile,
  addNoteFile,
  addSecurityFile,
  addAdditionalFile,
  addArchiveFile,
  addChecklistFile,
  moveFile,
  clearErrors,
  clearAllFiles
} from '../store/slices/documentsSlice';
import PanelButton from '../components/PanelButton';
import FileUpload from '../components/FileUpload';
import MultiFileUpload from '../components/MultiFileUpload';
import ProgressBar from '../components/ProgressBar';
import LoadingSpinner from '../components/LoadingSpinner';
import PageContainer from '../components/PageContainer';
import ChunkingModal from '../components/ChunkingModal';
import TifConversionModal from '../components/TifConversionModal';
import FileUploadProgressModal from '../components/FileUploadProgressModal';
import useProgress from '../hooks/useProgress';
import { apiFetch } from '../utils/apiUtils';
import api, { sessionManager } from '../services/api';
import socketService from '../services/socketService';
import { storeFile } from '../utils/fileStorage';


// Removed PageContainer and ContentWrapper - now using reusable PageContainer component

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const CompactButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &.primary {
    background: #3b82f6;
    color: white;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    
    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  }
`;

const DocumentScans = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state selectors
  const proposalFile = useSelector(state => state.documents.proposalFile);
  const noteFile = useSelector(state => state.documents.noteFile);
  const securityFile = useSelector(state => state.documents.securityFile);
  const proposalFiles = useSelector(state => state.documents.proposalFiles);
  const noteFiles = useSelector(state => state.documents.noteFiles);
  const securityFiles = useSelector(state => state.documents.securityFiles);
  const additionalFiles = useSelector(state => state.documents.additionalFiles);
  const archiveFiles = useSelector(state => state.documents.archiveFiles);
  const checklistFiles = useSelector(state => state.documents.checklistFiles);
  const errors = useSelector(state => state.documents.errors);

  // Local state
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(true);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesError, setDocumentTypesError] = useState(null);
  const [documentPriority, setDocumentPriority] = useState([]);
  const [loading, setLoading] = useState({
    extract: false
  });
  const [showChunkingModal, setShowChunkingModal] = useState(false);
  const [largeFiles, setLargeFiles] = useState([]);
  const [regularFiles, setRegularFiles] = useState([]);
  
  // TIF conversion modal state
  const [tifConversionModal, setTifConversionModal] = useState({
    isOpen: false,
    fileName: '',
    currentPage: 0,
    totalPages: 0,
    status: 'Preparing...',
    currentFileIndex: 0,
    totalFiles: 0,
    overallProgress: 0
  });

  // File upload progress modal state
  const [uploadProgressModal, setUploadProgressModal] = useState({
    isOpen: false,
    totalFiles: 0,
    uploadProgress: 0,
    status: 'Preparing upload...',
    uploadedBytes: 0,
    totalBytes: 0
  });

  const handleBackToSummary = () => {
    navigate(`/document-analysis/${loanId}`);
  };
  
  const { 
    progress, 
    status, 
    steps, 
    currentStep, 
    isActive, 
    resetProgress, 
    startProgress,
    completeProgress,
    updateProgress,
    getSessionId
  } = useProgress();

  // Clear errors on mount
  // Clear files when loan ID changes to prevent stale state
  useEffect(() => {
    dispatch(clearErrors());
    dispatch(clearAllFiles());
    // Reset progress to clean state when entering this page
    resetProgress();
  }, [loanId, dispatch, resetProgress]);

  // Fetch document types and priority order
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      setLoadingDocumentTypes(true);
      setDocumentTypesError(null);
      
      try {
        // Fetch document types
        const typesResponse = await apiFetch('/system-files/document-types');
        // apiFetch already returns parsed JSON
        const typesData = typesResponse;
        if (!typesData.success) {
          throw new Error('Document types request unsuccessful');
        }
        


        
        setDocumentTypes(typesData.document_types);

        // Fetch priority order
        const priorityResponse = await apiFetch('/system-files/default-document-priority');
        // apiFetch already returns parsed JSON
        const priorityData = priorityResponse;
        if (priorityData.success) {
          setDocumentPriority(priorityData.priority_order);
        }
        
        setLoadingDocumentTypes(false);
      } catch (error) {
        console.error('Error fetching document types:', error);
        setDocumentTypesError(error.message);
        setLoadingDocumentTypes(false);
      }
    };

    fetchDocumentTypes();
  }, []);

  const handleFileChange = (documentType, file) => {
    switch (documentType) {
      case 'proposal':
        dispatch(addProposalFile(file));
        break;
      case 'note':
        dispatch(addNoteFile(file));
        break;
      case 'security':
      case 'service_agreement':
        dispatch(addSecurityFile(file));
        break;
      case 'additional':
        dispatch(addAdditionalFile(file));
        break;
      case 'archive':
        dispatch(addArchiveFile(file));
        break;
      case 'checklist':
        dispatch(addChecklistFile(file));
        break;
      default:
        // For unmatched files, add to additional section
        console.log(`Unknown document type: ${documentType}, adding to additional`);
        dispatch(addAdditionalFile(file));
        break;
    }
  };

  const handleMoveFile = (file, fromType, toType, fromIndex) => {
    console.log(`Moving file from ${fromType} to ${toType}:`, file);
    dispatch(moveFile({ file, fromType, toType, fromIndex }));
  };

  // Function to check if a file needs chunking based on content type
  const checkIfFileNeedsChunking = async (file) => {
    try {
      // Check if it's a PDF by reading the file header
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Check for PDF magic bytes
      if (uint8Array.length >= 4) {
        const header = String.fromCharCode(uint8Array[0], uint8Array[1], uint8Array[2], uint8Array[3]);
        if (header === '%PDF') {
          console.log(`📄 Detected PDF content: ${file.name}`);
          return true; // PDFs need chunking
        }
      }
      
      // Check for TIFF magic bytes
      if (uint8Array.length >= 4) {
        const header = uint8Array[0] << 24 | uint8Array[1] << 16 | uint8Array[2] << 8 | uint8Array[3];
        if (header === 0x49492A00 || header === 0x4D4D002A) {
          console.log(`🖼️ Detected TIFF content: ${file.name}`);
          return true; // TIFFs need chunking
        }
      }
      
      console.log(`📄 File doesn't need chunking: ${file.name}`);
      return false;
    } catch (error) {
      console.error(`Error checking file type for ${file.name}:`, error);
      return false;
    }
  };

  /**
   * Fetch existing processed files for the loan and merge with new files
   * @param {Array} newFiles - New files being uploaded (largeFiles format)
   * @returns {Array} - Merged array of existing + new files
   */
  const fetchAndMergeExistingFiles = async (newFiles = []) => {
    try {
      console.log('🔍 [FETCH EXISTING] Fetching existing files for loan:', loanId);
      
      const response = await api.get(`/database/loan/${loanId}/documents`);
      
      if (!response.data.success || !response.data.data.documents) {
        console.log('⚠️ [FETCH EXISTING] No existing documents found');
        return newFiles;
      }
      
      const existingDocs = response.data.data.documents;
      console.log(`📄 [FETCH EXISTING] Found ${existingDocs.length} existing documents`);
      console.log('📄 [FETCH EXISTING] Raw documents from API:', existingDocs);
      
      // Transform existing docs to match largeFiles structure
      const existingFiles = existingDocs.map(doc => {
        console.log(`📄 [FETCH EXISTING] Processing doc: ${doc.fileName}, id: ${doc.id}, ignored: ${doc.ignored}, s3_textract_key: ${doc.s3_textract_key}`);
        return {
        filename: doc.fileName,
        page_count: 'Regular', // Existing files are already processed, treat as regular
        chunks: [], // Already processed, no chunking needed
        fileData: null, // No file data since it's already in S3
        fileType: doc.fileType,
        file_id: doc.id, // Database ID for toggling ignored state
        file_hash: doc.fileName, // Use filename as identifier
        isExisting: true, // Flag to identify existing files
        initialIgnoredState: doc.ignored || false, // Store initial ignored state from DB
        s3_textract_key: doc.s3_textract_key, // S3 key for backend to load
        file_type: doc.fileType // For backend format
      };
      });
      
      console.log('🔄 [FETCH EXISTING] Transformed existing files:', existingFiles);
      console.log('🔄 [FETCH EXISTING] New files:', newFiles);
      
      // Deduplicate: Remove new files that already exist in database (by filename)
      const existingFilenames = new Set(existingFiles.map(f => f.filename));
      const uniqueNewFiles = newFiles.filter(f => {
        const isDuplicate = existingFilenames.has(f.filename);
        if (isDuplicate) {
          console.log(`🔄 [FETCH EXISTING] Skipping duplicate new file: ${f.filename} (already in database)`);
        }
        return !isDuplicate;
      });
      
      // Merge existing + unique new files
      const mergedFiles = [...existingFiles, ...uniqueNewFiles];
      console.log(`✅ [FETCH EXISTING] Merged ${mergedFiles.length} total files (${existingFiles.length} existing + ${uniqueNewFiles.length} unique new, ${newFiles.length - uniqueNewFiles.length} duplicates removed)`);
      console.log('✅ [FETCH EXISTING] Merged files:', mergedFiles.map(f => ({ 
        filename: f.filename, 
        isExisting: f.isExisting, 
        file_id: f.file_id,
        initialIgnoredState: f.initialIgnoredState 
      })));
      
      return mergedFiles;
    } catch (error) {
      console.error('❌ [FETCH EXISTING] Error fetching existing files:', error);
      // On error, just return new files
      return newFiles;
    }
  };

  const handleViewExistingFiles = async () => {
    console.log('☁️ [VIEW EXISTING] Opening modal with existing files only...');
    
    try {
      // Fetch existing files (pass empty array for new files)
      const existingFiles = await fetchAndMergeExistingFiles([]);
      
      console.log(`☁️ [VIEW EXISTING] Found ${existingFiles.length} existing files`);
      
      // Open modal with existing files
      setLargeFiles(existingFiles);
      setRegularFiles([]);
      setShowChunkingModal(true);
    } catch (error) {
      console.error('❌ [VIEW EXISTING] Error fetching existing files:', error);
      alert(`Error loading existing files: ${error.message}`);
    }
  };

  const handleAutoPopulate = async () => {
    // Check if document types are loaded
    if (!documentTypes || documentTypes.length === 0) {
      console.error('❌ Document types not loaded yet');
      return;
    }

    // Create a hidden file input that accepts directories
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.directory = true;
    input.multiple = true;
    
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      
      if (files.length === 0) return;
      
      console.log(`📁 Found ${files.length} files in directory`);
      
      // Build categorized files object dynamically from loaded document types
      const categorizedFiles = {};
      documentTypes.forEach(docType => {
        if (docType.enabled) {
          categorizedFiles[docType.name] = [];
        }
      });
      categorizedFiles['additional'] = []; // Always have additional
      
      // Filter PDF and TIF files and categorize them
      let validFiles = files.filter(f => {
        const lowerName = f.name.toLowerCase();
        return lowerName.endsWith('.pdf') || lowerName.endsWith('.tif') || lowerName.endsWith('.tiff');
      });
      console.log(`📄 Filtered to ${validFiles.length} valid files (PDF/TIF)`);
      
      // Use shared processing function for optimization and detection
      console.log('🔧 [AUTOPOPULATE] Processing files...');
      let detectionResults;
      
      try {
        const { optimizedFiles, needsChunking, regularFiles } = await processAndDetectFiles(validFiles);
        
        // Map results back to original file structure for backward compatibility
        detectionResults = optimizedFiles.map(file => ({
          file,
          response: {
            needs_chunking: needsChunking.filter(item => 
              item.filename === file.name || item.filename === file.filename
            ),
            regular_files: regularFiles.filter(item => 
              item.filename === file.name || item.filename === file.filename
            )
          }
        }));
        
        // Update validFiles to use optimized files
        validFiles = optimizedFiles;
        
      } catch (error) {
        console.error('❌ [AUTOPOPULATE] Error processing files:', error);
        
        // Fallback to treating all files as regular
        detectionResults = validFiles.map(file => ({
          file,
          response: { needs_chunking: [], regular_files: [{ filename: file.name, page_count: 'Regular', fileType: file.type }] }
        }));
      }
      
      // Collect all results
      const allNeedsChunking = [];
      const allRegularFiles = [];
      
      // Collect all results
      detectionResults.forEach(({ file, response }) => {
        // Debug: Log file details
        console.log(`🔍 Processing file: ${file.name} (${file.type}) - Size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        if (file.previewUrl) {
          console.log(`🔍 File ${file.name} has preview URL: ${file.previewUrl}`);
        } else {
          console.log(`🔍 File ${file.name} has NO preview URL - may not be converted`);
        }
        
        if (response.needs_chunking && response.needs_chunking.length > 0) {
          // Attach file data to each large file
          const largeFilesWithData = response.needs_chunking.map(largefile => ({
            ...largefile,
            fileData: file
          }));
          allNeedsChunking.push(...largeFilesWithData);
        } else {
          // Push the actual response object with the original file
          console.log('🔍 Response structure:', response);
          if (response.regular_files && response.regular_files.length > 0) {
            const regularFile = response.regular_files[0];
            // Add the original file object (with previewUrl if it was converted)
            regularFile.fileData = file;
            allRegularFiles.push(regularFile);
          } else {
            // Fallback to filename if response structure is unexpected
            allRegularFiles.push({
              filename: file.name,
              fileData: file,
              page_count: 'Regular',
              fileType: file.type
            });
          }
        }
      });
      
      console.log('📊 AutoPopulate detection complete:', {
        needsChunking: allNeedsChunking.length,
        regularFiles: allRegularFiles.length
      });
      
      // Route ALL files through chunking modal for unified processing
      console.log('🔄 Routing all files through chunking modal for unified processing');
      console.log('🚨 Large files detected:', allNeedsChunking);
      console.log('📄 Regular files:', allRegularFiles);
      
      // Combine all files for the modal - chunked files + regular files
      const allFilesForModal = [
        ...allNeedsChunking,
        ...allRegularFiles.map(regularFile => {
          // Handle both string and object formats from backend
          const filename = typeof regularFile === 'string' ? regularFile : regularFile.filename;
          const fileType = typeof regularFile === 'object' ? regularFile.fileType : 'application/pdf';
          
          // Try multiple matching strategies to find the optimized file
          let matchingFile = validFiles.find(f => f.name === filename);
          
          // If not found, try matching the optimized filename (with _resized.jpg suffix)
          if (!matchingFile) {
            const optimizedFilename = filename.replace(/\.tiff?$/i, '_resized.jpg');
            matchingFile = validFiles.find(f => f.name === optimizedFilename);
          }
          
          // If still not found, try matching by original filename (without extension)
          if (!matchingFile) {
            const baseName = filename.replace(/\.tiff?$/i, '');
            matchingFile = validFiles.find(f => f.name.startsWith(baseName));
          }
          
          // Get page count from backend if available, otherwise estimate from file size
          let pageCount = 'Unknown';
          if (typeof regularFile === 'object' && regularFile.page_count) {
            pageCount = regularFile.page_count;
          } else if (matchingFile && matchingFile.size) {
            // Estimate pages based on file size (50KB per page)
            pageCount = Math.max(1, Math.floor(matchingFile.size / 50000));
          }
          console.log('🔍 Regular file mapping:', {
            regularFile,
            filename,
            pageCount,
            fileType,
            matchingFile: matchingFile ? matchingFile.name : 'NOT FOUND',
            hasPreviewUrl: matchingFile?.previewUrl ? 'YES' : 'NO',
            validFilesNames: validFiles.map(f => f.name),
            validFilesWithPreview: validFiles.filter(f => f.previewUrl).map(f => f.name),
            validFilesDetails: validFiles.map(f => ({ name: f.name, hasPreviewUrl: !!f.previewUrl, size: f.size }))
          });
          return {
            filename,
            page_count: pageCount,
            chunks: [],
            fileData: matchingFile, // This should contain the optimized file with previewUrl
            fileType
          };
        })
      ];
      
      console.log('🔍 All files for modal:', allFilesForModal);
      
      // Fetch existing files and merge with new ones
      const mergedFiles = await fetchAndMergeExistingFiles(allFilesForModal);
      
      setLargeFiles(mergedFiles);
      setRegularFiles([]); // No separate regular files since they're all in the modal
      setShowChunkingModal(true);
      return; // All files go through chunking modal
    };
    
    // Trigger the file picker
    input.click();
  };

  const handleExtractValues = async () => {
    if (!hasFiles) return;
    
    try {
      // Collect all files for chunking detection
      let allFiles = [
        ...proposalFiles,
        ...noteFiles,
        ...securityFiles,
        ...additionalFiles,
        ...archiveFiles,
        ...checklistFiles
      ].filter(f => f);
      
      // Debug: Log all files before processing
      console.log('🔍 [EXTRACTION DEBUG] All files before processing:', allFiles.map(f => ({
        name: f.name,
        size: f.size,
        sizeMB: (f.size / (1024 * 1024)).toFixed(2),
        type: f.type || 'unknown'
      })));
      
      // Note: TIF files are now converted to PDF by the backend, so no filtering needed
      
      // Debug: Log files for processing
      console.log('🔍 [EXTRACTION DEBUG] Files after filtering:', allFiles.map(f => ({
        name: f.name,
        size: f.size,
        sizeMB: (f.size / (1024 * 1024)).toFixed(2),
        type: f.type || 'unknown'
      })));
      
      console.log('🔍 Checking for large files...', allFiles.length, 'files');
      
      // Check if any files need chunking
      const formData = new FormData();
      
      // Debug: Check what properties the files have
      console.log('🔍 [EXTRACTION DEBUG] File properties:', allFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
        constructor: f.constructor.name,
        isFile: f instanceof File,
        keys: Object.keys(f)
      })));
      
      // Convert all files to File objects if needed (handles Redux state objects from drag-and-drop)
      const { getFile } = await import('../utils/fileStorage');
      const filesToProcess = allFiles.map(file => {
        if (file instanceof File) {
          return file;
        } else if (file.fileKey && file.isUploadedFile) {
          // Redux state object - retrieve actual File
          return getFile(file.fileKey) || file;
        }
        return file;
      }).filter(f => f instanceof File);
      
      if (filesToProcess.length === 0) {
        console.warn('⚠️ No processable files found');
        return;
      }
      
      filesToProcess.forEach(file => {
        formData.append('files', file);
      });
      formData.append('loan_id', loanId);
      
      let detectionResponse;
      try {
        detectionResponse = await api.post('/chunks/detect-large-files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('📊 Detection response:', detectionResponse.data);
      } catch (error) {
        console.error('🚨 [EXTRACTION DEBUG] 422 Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          files: allFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
        });
        
        // Log the full error response data
        console.error('🚨 [EXTRACTION DEBUG] Full error response data:', JSON.stringify(error.response?.data, null, 2));
        console.error('🚨 [EXTRACTION DEBUG] Full error object:', error);
        
        throw error;
      }
      
      if (detectionResponse.data.needs_chunking && detectionResponse.data.needs_chunking.length > 0) {
        // Large files detected - show chunking modal
        console.log('🚨 Large files detected:', detectionResponse.data.needs_chunking);
        
        // Attach file data to each large file for processing
        // Helper to robustly match backend filename to actual File objects
        const normalizeName = (name) => {
          return (name || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/_/g, ' ');
        };
        const getBase = (name) => normalizeName(name).replace(/\.(pdf|tif|tiff)$/i, '');
        const swapExtensions = (name) => {
          const base = name.replace(/\.(pdf|tif|tiff)$/i, '');
          return [
            `${base}.pdf`,
            `${base}.tif`,
            `${base}.tiff`
          ];
        };

        const tryMatchFile = (targetFilename) => {
          if (!targetFilename) return null;
          // Exact name match first
          let match = filesToProcess.find(f => f.name === targetFilename);
          if (match) return match;
          // Try extension swaps
          const candidates = swapExtensions(targetFilename);
          for (const cand of candidates) {
            match = filesToProcess.find(f => f.name === cand);
            if (match) return match;
          }
          // Normalized base-name match
          const targetBase = getBase(targetFilename);
          match = filesToProcess.find(f => getBase(f.name) === targetBase);
          if (match) return match;
          // Starts-with base-name fallback
          match = filesToProcess.find(f => getBase(f.name).startsWith(targetBase));
          return match || null;
        };

        const largeFilesWithData = detectionResponse.data.needs_chunking.map(largefile => {
          const matchingFile = tryMatchFile(largefile.filename);
          if (!matchingFile) {
            console.warn('⚠️ Could not match file for needs_chunking entry; skipping payload attach', {
              backendFilename: largefile.filename,
              availableFiles: filesToProcess.map(f => f.name)
            });
          }
          return {
            ...largefile,
            fileData: matchingFile || null
          };
        });
        
        // Fetch existing files and merge with new ones
        const mergedFiles = await fetchAndMergeExistingFiles(largeFilesWithData);
        
        setLargeFiles(mergedFiles);
        setRegularFiles(detectionResponse.data.regular_files);
        setShowChunkingModal(true);
      } else {
        // No large files - proceed with normal extraction
        console.log('✅ No large files - proceeding with normal extraction');
        await proceedWithExtraction();
      }
    } catch (error) {
      console.error('❌ Error in handleExtractValues:', error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  const proceedWithExtraction = async (filesForExtraction = null, selectedDocuments = null) => {
    // Reset progress and start tracking
    resetProgress();
    startProgress([
      'Upload Documents to Storage',
      'OCR Documents',
      'Extract Values From Documents',
      'Match Values to Core System',
      'Complete'
    ]);
    
    try {
      // ⏳ Wait for WebSocket connection to be ready with better error handling
      let socketConnected = false;
      try {
        await socketService.waitForConnection(5000); // 5 second timeout
        socketConnected = true;
        console.log('✅ [DocumentScans] WebSocket connection ready');
      } catch (error) {
        console.warn('⚠️ [DocumentScans] WebSocket connection timeout, proceeding without real-time updates:', error);
        // Continue with extraction even if Socket.IO is not available
        // The backend will still work, just without real-time progress updates
      }
      
      // Log selected documents if provided
      if (selectedDocuments) {
        console.log('📋 [PROCEED EXTRACTION] Using selected documents:', selectedDocuments.length);
        console.log('📋 [PROCEED EXTRACTION] Selected document details:', selectedDocuments.map(d => ({ 
          fileName: d.fileName, 
          id: d.id, 
          s3_textract_key: d.s3_textract_key 
        })));
      }
      
      // Use provided files or fall back to Redux state
      const files = filesForExtraction || {
        proposalFile: proposalFile,
        noteFile: noteFile,
        securityFile: securityFile,
        // Add all files for each type so backend can handle multiple files
        proposalFiles: proposalFiles,
        noteFiles: noteFiles,
        securityFiles: securityFiles,
        archiveFiles: archiveFiles,
        checklistFiles: checklistFiles,
        additionalFiles: additionalFiles
      };
      
      // Validation: Ensure we have files to process OR loan_id for loading from S3
      const totalFiles = files ? (
        (files.proposalFiles?.length || 0) + 
        (files.noteFiles?.length || 0) + 
        (files.securityFiles?.length || 0) + 
        (files.additionalFiles?.length || 0) + 
        (files.archiveFiles?.length || 0) + 
        (files.checklistFiles?.length || 0) +
        (files.proposalFile ? 1 : 0) +
        (files.noteFile ? 1 : 0) +
        (files.securityFile ? 1 : 0)
      ) : 0;
      
      // Allow extraction with no files if we have a loan_id (load from S3)
      if (totalFiles === 0 && !loanId) {
        alert('No files to process. Please upload files first.');
        return;
      }
      
      if (totalFiles === 0 && loanId) {
        console.log('✅ No files provided - will load existing textract files from S3 for loan:', loanId);
      }
      
      // Pass selected documents if provided (for filtering which files to load from S3)
      const extractionParams = { files, loanId };
      if (selectedDocuments) {
        extractionParams.selectedDocuments = selectedDocuments;
        console.log('📋 [PROCEED EXTRACTION] Passing selected documents to extraction API:', selectedDocuments.length);
      }
      
      await dispatch(extractValues(extractionParams)).unwrap();
      
      // Complete progress
      completeProgress();
      
      // Navigate back to main document analysis after successful extraction
      navigate(`/document-analysis/${loanId}?extraction=true`);
    } catch (error) {
      console.error('❌ [DocumentScans] Extraction failed:', error);
      
      // Reset progress and show user-friendly error
      resetProgress();
      
      // Show specific error message based on error type
      let errorMessage = 'Extraction failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error to user
      alert(`❌ ${errorMessage}`);
      
      // If it's a network error, suggest checking connection
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('fetch')) {
        console.warn('🌐 [DocumentScans] Network error detected, check API server connection');
      }
    }
  };
  
  const handleChunkingComplete = async (unmutedFiles) => {
    console.log('🎯 [CHUNKING COMPLETE] ===== RUN MATCH PROCESS CLICKED =====');
    console.log('✅ All chunks processed - proceeding with extraction');
    console.log('🔍 [CHUNKING COMPLETE] Received unmuted files:', unmutedFiles?.length || 'none');
    console.log('🔍 [CHUNKING COMPLETE] Unmuted files details:', unmutedFiles?.map(f => ({ 
      filename: f.filename, 
      isExisting: f.isExisting,
      hasFileData: !!f.fileData,
      file_id: f.file_id
    })));
    setShowChunkingModal(false);
    resetProgress(); // Reset progress bar when modal closes
    
    if (unmutedFiles && unmutedFiles.length > 0) {
      // Categorize the unmuted files back into their respective arrays
      const newProposalFiles = [];
      const newNoteFiles = [];
      const newSecurityFiles = [];
      const newArchiveFiles = [];
      const newChecklistFiles = [];
      const newAdditionalFiles = [];
      
      // Import fileStorage once
      const { getFile } = await import('../utils/fileStorage');
      
      for (const file of unmutedFiles) {
        // Skip existing files - they're already in S3 and don't need to be added to Redux state
        if (file.isExisting) {
          console.log(`✅ [CHUNKING COMPLETE] Existing file ${file.filename} is unmuted - will be included in extraction`);
          continue;
        }
        
        const fileType = file.file_type || 'additional';
        const fileData = file.fileData;
        
        if (!fileData) {
          console.warn(`⚠️ Skipping NEW file ${file.filename} - no file data`);
          continue;
        }
        
        // Convert Redux state object to File if needed
        const fileObject = fileData instanceof File 
          ? fileData 
          : (fileData.fileKey ? getFile(fileData.fileKey) : fileData);
        
        if (!fileObject) {
          console.warn(`⚠️ Skipping NEW file ${file.filename} - could not retrieve File object`);
          continue;
        }
        
        switch (fileType) {
          case 'proposal':
            newProposalFiles.push(fileObject);
            break;
          case 'note':
            newNoteFiles.push(fileObject);
            break;
          case 'service_agreement':
            newSecurityFiles.push(fileObject);
            break;
          case 'archive':
            newArchiveFiles.push(fileObject);
            break;
          case 'checklist':
            newChecklistFiles.push(fileObject);
            break;
          case 'additional':
          default:
            newAdditionalFiles.push(fileObject);
            break;
        }
      }
      
      // Update Redux state with filtered files
      dispatch(setProposalFiles(newProposalFiles));
      dispatch(setNoteFiles(newNoteFiles));
      dispatch(setSecurityFiles(newSecurityFiles));
      dispatch(setArchiveFiles(newArchiveFiles));
      dispatch(setChecklistFiles(newChecklistFiles));
      dispatch(setAdditionalFiles(newAdditionalFiles));
      
      // DO NOT pass files - they're already processed in S3!
      // Chunking modal already ran OCR, so we just need to load existing textract files
      console.log('✅ [CHUNKING COMPLETE] Files already processed - skipping upload/OCR');
      console.log('✅ [CHUNKING COMPLETE] Will load existing textract.json files from S3 by loan_id');
      
      // Build selected documents array for backend (only existing files need this)
      const selectedDocuments = unmutedFiles
        .filter(f => f.isExisting) // Only existing files need document info
        .map(f => ({
          id: f.file_id,
          fileName: f.filename,
          fileType: f.file_type || f.fileType,
          file_type: f.file_type || f.fileType,
          s3_textract_key: f.s3_textract_key
        }));
      
      console.log('📋 [CHUNKING COMPLETE] Built selectedDocuments:', selectedDocuments.length);
      console.log('📋 [CHUNKING COMPLETE] Selected documents:', selectedDocuments);
      
      // Proceed with extraction WITHOUT files (backend will load from S3 by selected documents)
      await proceedWithExtraction(null, selectedDocuments);
    }
  };
  
  const handleChunkingCancel = () => {
    console.log('❌ Chunking cancelled');
    setShowChunkingModal(false);
    setLargeFiles([]);
    setRegularFiles([]);
    resetProgress(); // Reset progress bar when modal closes
  };

  // Shared function to process and detect files (used by both dropzone and autopopulate)
  const processAndDetectFiles = async (filesToProcess) => {
    console.log(`🔧 [PROCESS FILES] Starting optimization and detection for ${filesToProcess.length} file(s)`);
    
    // Optimize files before processing (compress large images, convert TIF to PDF)
    const optimizedFiles = [];
    
    // Check if any TIF files need conversion
    const tifFiles = filesToProcess.filter(f => {
      const lowerName = f.name.toLowerCase();
      return lowerName.endsWith('.tif') || lowerName.endsWith('.tiff');
    });
    
    // Show modal if there are TIF files to convert
    const hasTifFiles = tifFiles.length > 0;
    if (hasTifFiles) {
      setTifConversionModal({
        isOpen: true,
        fileName: '',
        currentPage: 0,
        totalPages: 0,
        status: `Preparing to convert ${tifFiles.length} file(s)...`,
        currentFileIndex: 0,
        totalFiles: tifFiles.length,
        overallProgress: 0
      });
    }
    
    let tifFileIndex = 0;
    let filesCompleted = 0;
    
    for (const file of filesToProcess) {
      try {
        const { optimizeFileForUpload } = await import('../utils/fileOptimization');
        
        const isTif = tifFiles.includes(file);
        let onProgress = null;
        
        if (isTif) {
          tifFileIndex++;
          const overallProgress = tifFiles.length > 0 
            ? Math.round((filesCompleted / tifFiles.length) * 100)
            : 0;
          
          setTifConversionModal(prev => ({
            ...prev,
            fileName: file.name,
            currentFileIndex: tifFileIndex,
            currentPage: 0,
            totalPages: 0,
            status: `Converting file ${tifFileIndex} of ${tifFiles.length}...`,
            overallProgress
          }));
          
          onProgress = (currentPage, totalPages, status) => {
            if (totalPages > 0) {
              setTifConversionModal(prev => ({
                ...prev,
                currentPage,
                totalPages,
                status: status || `Processing page ${currentPage} of ${totalPages}...`
              }));
            } else {
              setTifConversionModal(prev => ({
                ...prev,
                status: status || `Converting ${file.name}...`
              }));
            }
          };
        }
        
        const optimizedFile = await optimizeFileForUpload(file, onProgress);
        
        if (isTif) {
          filesCompleted++;
          const overallProgress = tifFiles.length > 0 
            ? Math.round((filesCompleted / tifFiles.length) * 100)
            : 0;
          
          setTifConversionModal(prev => ({
            ...prev,
            status: `✅ Conversion complete for ${file.name}`,
            currentPage: prev.totalPages || 0,
            totalPages: prev.totalPages || 0,
            overallProgress
          }));
        }
        
        optimizedFiles.push(optimizedFile);
        
      } catch (error) {
        console.error(`❌ Failed to optimize ${file.name}:`, error);
        optimizedFiles.push(file); // Use original file if optimization fails
      }
    }
    
    // Close TIF conversion modal
    if (hasTifFiles) {
      setTifConversionModal(prev => ({
        ...prev,
        isOpen: false
      }));
    }
    
    // Call detection API
    const formData = new FormData();
    optimizedFiles.forEach(file => {
      formData.append('files', file);
    });
    formData.append('loan_id', loanId);
    
    const detectionResponse = await api.post('/chunks/detect-all-large-files', formData);
    console.log('📊 [PROCESS FILES] Detection response:', detectionResponse.data);
    
    return {
      optimizedFiles,
      needsChunking: detectionResponse.data.needs_chunking || [],
      regularFiles: detectionResponse.data.regular_files || []
    };
  };

  const handleAddFileToChunkingModal = async (fileType, file) => {
    console.log(`📎 [ADD TO CHUNKING] Adding ${file.name} (type: ${fileType}) to chunking modal`);
    
    try {
      // Use shared processing function
      const { optimizedFiles, needsChunking, regularFiles } = await processAndDetectFiles([file]);
      
      const optimizedFile = optimizedFiles[0];
      const chunkingFile = needsChunking.find(item => 
        item.filename === optimizedFile.name || item.filename === file.name
      );
      const regularFile = regularFiles.find(item => 
        item.filename === optimizedFile.name || item.filename === file.name
      );
      
      let fileForModal;
      
      if (chunkingFile) {
        console.log(`📄 [ADD TO CHUNKING] Large file: ${chunkingFile.page_count} pages, ${chunkingFile.chunks.length} chunks`);
        fileForModal = {
          ...chunkingFile,
          fileData: optimizedFile,
          fileType: fileType
        };
      } else if (regularFile) {
        console.log(`📄 [ADD TO CHUNKING] Regular file: ${regularFile.page_count} pages`);
        fileForModal = {
          filename: optimizedFile.name,
          page_count: regularFile.page_count || 'Regular',
          chunks: [],
          fileData: optimizedFile,
          fileType: fileType
        };
      } else {
        // Fallback
        fileForModal = {
          filename: optimizedFile.name,
          page_count: 'Regular',
          chunks: [],
          fileData: optimizedFile,
          fileType: fileType
        };
      }
      
      // Fetch existing files and merge with new one
      const mergedFiles = await fetchAndMergeExistingFiles([fileForModal]);
      
      setLargeFiles(mergedFiles);
      setRegularFiles([]);
      setShowChunkingModal(true);
      
      console.log(`✅ [ADD TO CHUNKING] Chunking modal opened with file:`, fileForModal);
      
    } catch (error) {
      console.error('❌ [ADD TO CHUNKING] Error processing file:', error);
      alert(`Failed to process file: ${error.response?.data?.detail || error.message}`);
    }
  };


  // Helper function to get accept string based on document style
  const getAcceptString = (documentStyle) => {
    if (documentStyle === 'form') {
      return '.pdf,.csv,.txt';
    } else {
      return '.pdf,.txt';
    }
  };

  // Check if any files exist - cover all supported document types
  const hasFiles = proposalFile || noteFile || securityFile || 
                   proposalFiles?.length > 0 || 
                   noteFiles?.length > 0 || 
                   securityFiles?.length > 0 ||
                   additionalFiles?.length > 0 || 
                   archiveFiles?.length > 0 || 
                   checklistFiles?.length > 0;

  return (
    <PageContainer>
        <Title>
          Document Analysis - Loan ID: {loanId}
        </Title>
        
        {isActive ? (
          /* Progress Bar - Show when processing */
          <Section>
            <ProgressBar 
              progress={progress}
              status={status}
              steps={steps}
              currentStep={currentStep}
            />
          </Section>
        ) : (
          /* Upload Interface - Show when not processing */
          <>
            {/* Pro Tip */}
        <Section>
          <div style={{ 
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                background: '#fbbf24', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                💡
              </div>
              <strong style={{ color: '#1e40af' }}>Pro Tip:</strong>
            </div>
            <p style={{ color: '#1e40af', margin: 0, fontSize: '0.875rem' }}>
              Click "Auto-Populate" to select a folder from your computer. The system will automatically categorize PDF files based on their names (e.g., "proposal.pdf", "note.pdf", "security.pdf").
            </p>
          </div>
        </Section>

        {/* Auto-Populate from Directory */}
        <Section>
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <button
              onClick={handleAutoPopulate}
              disabled={loadingDocumentTypes || documentTypes.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                background: loadingDocumentTypes || documentTypes.length === 0 ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loadingDocumentTypes || documentTypes.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '280px',
                transition: 'all 0.2s',
                opacity: loadingDocumentTypes || documentTypes.length === 0 ? 0.6 : 1
              }}
            >
              <FolderPlus size={16} />
              Auto-Populate from Folder
            </button>
            
            {/* View Existing Files Button */}
            <button
              onClick={handleViewExistingFiles}
              title="View existing processed files"
              style={{
                padding: '0.75rem',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
            >
              <Cloud size={16} />
            </button>
          </div>
        </Section>

        {/* Upload Section */}
        <Section>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {(() => {
              // Show loading state
              if (loadingDocumentTypes) {
                return (
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    textAlign: 'center', 
                    padding: '3rem',
                    color: '#6b7280'
                  }}>
                    <LoadingSpinner />
                    <div style={{ marginTop: '1rem' }}>Loading document types...</div>
                  </div>
                );
              }
              
              // Show error state
              if (documentTypesError) {
                return (
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    textAlign: 'center', 
                    padding: '3rem',
                    color: '#dc2626',
                    background: '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Error Loading Document Types
                    </div>
                    <div>{documentTypesError}</div>
                  </div>
                );
              }
              
              // Show message if no document types configured
              if (documentTypes.length === 0) {
                return (
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    textAlign: 'center', 
                    padding: '3rem',
                    color: '#d97706',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    border: '1px solid #fde68a'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      No Document Types Configured
                    </div>
                    <div>Please configure document types in System Files before uploading documents.</div>
                  </div>
                );
              }
              
              return documentPriority.map((documentName) => {
                const docType = documentTypes.find(dt => dt.name === documentName);
                if (!docType) {
                  return null;
                }

                // Get the corresponding files from Redux state
                let files = [];
                let primaryFile = null;
                switch (documentName) {
                  case 'proposal':
                    files = proposalFiles;
                    primaryFile = proposalFile;
                    break;
                  case 'note':
                    files = noteFiles;
                    primaryFile = noteFile;
                    break;
                  case 'service_agreement':
                    files = securityFiles;
                    primaryFile = securityFile;
                    break;
                  case 'additional':
                    files = additionalFiles;
                    primaryFile = additionalFiles[0] || null;
                    break;
                  case 'archive':
                    files = archiveFiles;
                    primaryFile = archiveFiles[0] || null;
                    break;
                  case 'checklist':
                    files = checklistFiles;
                    primaryFile = checklistFiles[0] || null;
                    break;
                  default:
                    // For new document types, we'll need to extend Redux state
                    break;
                }

                return (
                  <MultiFileUpload 
                    key={documentName}
                    fileType={documentName}
                    label={docType.display_name}
                    files={files}
                    primaryFile={primaryFile}
                    onChange={(fileType, file) => handleFileChange(fileType, file)}
                    onMoveFile={handleMoveFile}
                    onAddToChunkingModal={handleAddFileToChunkingModal}
                    accept={getAcceptString(docType.document_style)}
                  />
                );
              });
            })()}
          </div>
        </Section>

        {/* Extract Values Button */}
        <Section>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <CompactButton 
              className="primary"
              onClick={handleExtractValues}
              disabled={loading.extract || !hasFiles}
              style={{
                padding: '1rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                minWidth: '200px'
              }}
            >
              <Zap size={18} />
              {loading.extract ? 'Extracting...' : 'Extract Values'}
            </CompactButton>
            {!hasFiles && (
              <div style={{ 
                marginTop: '0.75rem', 
                color: '#6b7280', 
                fontSize: '0.875rem' 
              }}>
                Upload documents above to enable extraction
              </div>
            )}
          </div>
        </Section>
          </>
        )}

        {errors.extract && (
          <div style={{ 
            color: '#dc2626', 
            fontSize: '0.875rem', 
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            Extraction Error: {errors.extract}
          </div>
        )}
        
        {/* TIF Conversion Modal */}
        <TifConversionModal
          isOpen={tifConversionModal.isOpen}
          fileName={tifConversionModal.fileName}
          currentPage={tifConversionModal.currentPage}
          totalPages={tifConversionModal.totalPages}
          status={tifConversionModal.status}
          currentFileIndex={tifConversionModal.currentFileIndex}
          totalFiles={tifConversionModal.totalFiles}
          overallProgress={tifConversionModal.overallProgress}
        />
        
        {/* File Upload Progress Modal */}
        <FileUploadProgressModal
          isOpen={uploadProgressModal.isOpen}
          onClose={() => setUploadProgressModal(prev => ({ ...prev, isOpen: false }))}
          totalFiles={uploadProgressModal.totalFiles}
          uploadProgress={uploadProgressModal.uploadProgress}
          status={uploadProgressModal.status}
          uploadedBytes={uploadProgressModal.uploadedBytes}
          totalBytes={uploadProgressModal.totalBytes}
        />
        
        {/* Chunking Modal */}
        {showChunkingModal && (
          <ChunkingModal
            largeFiles={largeFiles}
            onComplete={handleChunkingComplete}
            onCancel={handleChunkingCancel}
            loanId={loanId}
          />
        )}

    </PageContainer>
  );
};

export default DocumentScans;
